import React from "react";
import { cn } from "@/lib/utils";
import { cva, VariantProps } from "class-variance-authority";
import {
  format as dfFormat,
  isValid as dfIsValid,
  parse as dfParse,
  isSameMonth,
  startOfMonth,
} from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const dateGroupVariants = cva("flex items-center justify-between w-full bg-input/30 rounded-md", {
  variants: { variant: { default: "border shadow-sm", inline: "" }, size: { default: "", sm: "" } },
  defaultVariants: { variant: "default", size: "default" },
});
const dateInputVariants = cva("flex items-center py-1 text-sm w-full", {
  variants: { variant: { default: "px-2", inline: "" }, size: { default: "h-9", sm: "h-8" } },
  defaultVariants: { variant: "default", size: "default" },
});

interface DateInputProps extends VariantProps<typeof dateGroupVariants> {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  inputClassName?: string;
  buttonClassName?: string;
  disabled?: boolean;
  placeholder?: string;
}

const DISPLAY_FMT = "dd/MM/yyyy";
const toDisplay = (d?: Date | null) => (d && dfIsValid(d) ? dfFormat(d, DISPLAY_FMT) : "");
const parseStrict = (s: string): Date | undefined => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return undefined;
  const dd = +m[1], mm = +m[2], yyyy = +m[3];
  const d = new Date(yyyy, mm - 1, dd);
  return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd ? d : undefined;
};


const maskDDMMYYYY = (raw: string) => {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  let out = "";
  for (let i = 0; i < digits.length; i++) {
    out += digits[i];
    if (i === 1 || i === 3) out += "/";
  }
  return { masked: out, digits };
};
const caretFromDigits = (masked: string, digitsCount: number) => {
  let count = 0;
  for (let i = 0; i < masked.length; i++) {
    if (/\d/.test(masked[i])) count++;
    if (count === digitsCount) return i + 1;
  }
  return masked.length;
};

export const DateInput: React.FC<DateInputProps> = ({
  date: defaultDate,
  onDateChange,
  disabled,
  variant,
  size,
  inputClassName,
  buttonClassName,
  placeholder = "dd/mm/yyyy",
}) => {
  const [date, setDate] = React.useState<Date | undefined>(defaultDate);
  const [text, setText] = React.useState<string>(toDisplay(defaultDate));
  const [open, setOpen] = React.useState(false);

  const [month, setMonth] = React.useState<Date>(startOfMonth(defaultDate ?? new Date()));

  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    setDate(defaultDate);
    setText(toDisplay(defaultDate));
    if (defaultDate && !isSameMonth(month, defaultDate)) {
      setMonth(startOfMonth(defaultDate));
    }
  }, [defaultDate]);

  const commit = (d?: Date) => {
    setDate(d);
    setText(toDisplay(d));
    onDateChange?.(d);
    if (d && !isSameMonth(month, d)) {
      setMonth(startOfMonth(d));
    }
  };

  const handleSelect = (d?: Date) => {
    commit(d);
    setOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    const prev = el.value;
    const sel = el.selectionStart ?? prev.length;
    const digitsBefore = prev.slice(0, sel).replace(/\D/g, "").length;

    const { masked, digits } = maskDDMMYYYY(prev);
    setText(masked);

    requestAnimationFrame(() => {
      const pos = caretFromDigits(masked, digitsBefore);
      inputRef.current?.setSelectionRange(pos, pos);
    });

    if (digits.length === 8) {
      const parsed = parseStrict(masked);
      if (parsed) commit(parsed);
    }
  };

  const handleBlur = () => {
    const parsed = parseStrict(text);
    if (!parsed) {
      setText(toDisplay(date)); // snap back to last valid
    } else if (!date || +parsed !== +date) {
      commit(parsed);
    }
  };

  return (
    <div className={cn("w-full", disabled && "opacity-50 cursor-not-allowed")}>
      <div className={cn(dateGroupVariants({ variant, size }))}>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={cn(
            dateInputVariants({ variant, size }),
            "bg-transparent outline-none",
            inputClassName
          )}
        />

        <Popover
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (o) {
              // when opening, show the relevant month
              setMonth(startOfMonth(date ?? new Date()));
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className={cn("w-8 h-8 -ml-1", buttonClassName)}
              disabled={disabled}
            >
              <CalendarIcon className="w-4 h-4" />
            </Button>
          </PopoverTrigger>

          <PopoverContent className="p-0 z-[9999]" align="end">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleSelect}
              month={month}
              onMonthChange={(m) => setMonth(startOfMonth(m))}
              showOutsideDays
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};




// import React, { useEffect } from "react";
// import { Popover, PopoverTrigger, PopoverContent } from "./popover";
// import { Input } from "./input";
// import { Button } from "./button";
// import { CalendarIcon } from "lucide-react";
// import { Calendar } from "./calendar";
// import { format, isValid } from "date-fns";
// import { cn } from "@/lib/utils";
// import { useMaskito } from "@maskito/react"
// import { maskitoDateOptionsGenerator, maskitoWithPlaceholder } from '@maskito/kit';
// import type {MaskitoOptions} from '@maskito/core';
// import { newDate } from "@/lib/helpers";

// interface DateInputProps {
//     date?: Date | string
//     onDateChange?: (date:Date | undefined) => void
//     containerClassName?: string
//     inputClassName?: string
//     buttonClassName?: string
//     disabled?: boolean
// }

// export function DateInput({ date:stateDate, onDateChange=()=>{}, containerClassName, inputClassName, buttonClassName, disabled=false }:DateInputProps) {
//     const PLACEHOLDER = 'dd/mm/yyyy';

//     const dateOptions = maskitoDateOptionsGenerator({ 
//         mode: 'dd/mm/yyyy', 
//         separator: '/',
//     });

//     const {
//         plugins, // plugins keeps caret inside actual value and remove placeholder on blur
//         ...placeholderOptions
//         // pass 'true' as second argument to add plugin to hide placeholder when input is not focused
//     } = maskitoWithPlaceholder(PLACEHOLDER, true);

//     const options = {
//         ...dateOptions,
//         plugins: plugins.concat(dateOptions.plugins || []),
//         preprocessors: [
//             // Always put it BEFORE all other preprocessors
//             ...placeholderOptions.preprocessors,
//             ...dateOptions.preprocessors,
//         ],
//         postprocessors: [
//             ...dateOptions.postprocessors,
//             // Always put it AFTER all other postprocessors
//             ...placeholderOptions.postprocessors,
//         ]
//     } satisfies Required<MaskitoOptions>;


//     const maskedInputRef = useMaskito({ options });

//     const [date, setDate] = React.useState<Date | undefined>(stateDate ? newDate(stateDate) : undefined);
//     const [stringDate, setStringDate] = React.useState<string>("");
//     const [errorMessage, setErrorMessage] = React.useState<string>("");

//     React.useEffect(()=>{
//         if (stateDate && isValid(stateDate)) {
//             setStringDate(format(stateDate, "dd/MM/yyyy"));
//         }
//     }, [])

//     useEffect(()=>{
//         if(stateDate && date !== stateDate){
//             setDate(stateDate ? newDate(stateDate) : undefined)
//             setStringDate(format(stateDate, "dd/MM/yyyy"));
//         }
//     }, [stateDate])

//     return (
//         <Popover>
//             <div className={cn("relative w-full", containerClassName)}>
//                 <Input
//                     disabled={disabled}
//                     type="text"
//                     onInput={(e)=>{
//                         const [day, month, year] = e.currentTarget.value.split("/").map(Number);
//                         const parsedDate = new Date(year, month - 1, day);
//                         if(!isValid(parsedDate)){
//                             onDateChange(undefined)
//                         } else {
//                             setErrorMessage("");
//                             setDate(parsedDate);
//                             onDateChange(parsedDate)
                            
//                         }
//                         setStringDate(e.currentTarget.value)
//                     }}
//                     value={stringDate}
//                     onBlur={(e)=>{
//                         const [day, month, year] = e.currentTarget.value.split("/").map(Number);
//                         const parsedDate = new Date(year, month - 1, day);
//                         if(!isValid(parsedDate)){
//                             setErrorMessage("Invalid Date");
//                             setDate(undefined);
//                             onDateChange(undefined)
//                         }
//                     }}
//                     placeholder="dd/mm/yyyy"
//                     ref={maskedInputRef}
//                     className={cn(``, inputClassName)}
//                 />
//                 <PopoverTrigger asChild>
//                     <Button
//                         variant={"outline"}
//                         className={cn(
//                             "font-normal absolute right-0 translate-y-[-50%] top-[50%] rounded-l-none",
//                             !date && "text-muted-foreground",
//                             buttonClassName
//                         )}
//                     >
//                         <CalendarIcon className="w-4 h-4 text-neutral-600" strokeWidth={1.5}/>
//                     </Button>
//                 </PopoverTrigger>
//             </div>
//             <PopoverContent className="w-auto p-0">
//                 <Calendar
//                     mode="single"
//                     selected={date}
//                     onSelect={(selectedDate) => {
//                         if (!selectedDate) return;
//                         setDate(selectedDate);
//                         onDateChange(selectedDate)
//                         setStringDate(format(selectedDate, "dd/MM/yyyy"));
//                         setErrorMessage("");
//                     }}
//                     defaultMonth={date}
//                     initialFocus
//                 />
//             </PopoverContent>
//         </Popover>
//     );
// }